/**
 * Display sizes, rendered in the browser.
 *
 * A public page asks for an image at one of `FILE_IMAGE_WIDTHS` — `?w=` on its
 * `contentUrl` — and the API serves the smallest stored size that covers it,
 * or the original when none does. The API never holds an image's bytes, so it
 * cannot resize one; the sizes are made here, as previews are, and `upload.ts`
 * stores them beside the file.
 */

import { FILE_IMAGE_WIDTHS, type FileImageWidth } from '@mds/shared/types/file';

import { drawTo, encodeWithin } from './transcode';

/** The API's ceiling. A 1920-pixel photograph lands at a few hundred KB. */
const MAX_VARIANT_BYTES = 2 * 1024 * 1024;

/** Below an upload's: these are for looking at on a page, and the original keeps the detail. */
const QUALITY_LADDER = [0.8, 0.7, 0.6];

export interface ImageVariant {
  width: FileImageWidth;
  blob: Blob;
}

/**
 * A WebP of `source` at each display width it reaches. The smallest is always
 * made — an image narrower than that is stored at its own width in that slot —
 * so every image ends up with at least one size and the Files page knows it is
 * done with it. Past the image's own width nothing is made: the original is
 * already that small, and the API serves it for those widths.
 */
export async function makeVariants(source: Blob): Promise<ImageVariant[]> {
  // `from-image` for files uploaded before uploads were re-encoded, which may
  // still carry an EXIF rotation.
  const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });
  try {
    const variants: ImageVariant[] = [];
    for (const [index, width] of FILE_IMAGE_WIDTHS.entries()) {
      if (index > 0 && width > bitmap.width) break;
      const drawnWidth = Math.min(width, bitmap.width);
      const drawnHeight = Math.max(1, Math.round((bitmap.height * drawnWidth) / bitmap.width));
      variants.push({
        width,
        blob: await encodeWithin(
          drawTo(bitmap, drawnWidth, drawnHeight),
          MAX_VARIANT_BYTES,
          QUALITY_LADDER,
        ),
      });
    }
    return variants;
  } finally {
    bitmap.close();
  }
}
